<template>
    <div class="relative bg-[url(/images/games/ps-99/background.png)] bg-cover">
        <div class="bg-black/90">
            <div class="relative mx-auto max-w-7xl py-10 lg:py-16 px-6 text-center lg:px-8">
                <div class="mx-auto max-w-6xl">
                    <div class="flex gap-x-3 justify-center items-center">
                        <NuxtImg src="/images/games/icons/ps99.png" class="h-10" />
                        <h1 class="text-xl lg:text-3xl text-white font-bold">Pet Simulator 99</h1>
                    </div>
                </div>
                <p class="mt-3.5 mx-auto max-w-5xl font-medium lg:text-md leading-7 text-white/60 hidden md:block">Pet Simulator 99 is a ROBLOX game where players collect, train, and upgrade pets to earn coins and rewards. Explore various worlds, complete challenges, and trade pets with others to build the ultimate pet collection.</p>
            </div>
        </div>
    </div>
    <NuxtLayout name="ads">
        <div class="grid grid-cols-3 sm:auto-fill-[150px] gap-3.5 justify-center">
            <NuxtLink to="/ps-99/items">
                <NuxtImg class="transition-transform hover:translate-y-[-5px] rounded-md" src="/images/games/mm2/items.png" />
            </NuxtLink>
            <NuxtLink to="/ps-99/calculate">
                <NuxtImg class="transition-transform hover:translate-y-[-5px] rounded-md" src="/images/games/da-hood/trade-calculator.png" />
            </NuxtLink>
            <NuxtLink to="/ps-99/ads">
                <NuxtImg class="transition-transform hover:translate-y-[-5px] rounded-md" src="/images/games/da-hood/trade-ads.png" />
            </NuxtLink>
            <NuxtLink to="/ps-99/value-changes">
                <NuxtImg class="transition-transform hover:translate-y-[-5px] rounded-md" src="/images/games/da-hood/value-changes.png" />
            </NuxtLink>
        </div>
        <div class="mt-6 space-y-6">
            <section id="latestItems">
                <h2 class="text-md text-gray-800 dark:text-white font-semibold">Latest Items</h2>
                <div class="relative mt-3.5">
                    <Swiper :navigation="{ nextEl: '.ps99ItemsNext', prevEl: '.ps99ItemsPrev', disabledClass: 'carouselButtonDisabled' }" :modules="[SwiperNavigation, SwiperGrid]" :breakpoints="itemBreakpoints">
                        <SwiperSlide v-for="(item, index) in items" :key="item.id">
                            <NuxtLink :to="`/ps-99/item/${item.id}`">
                                <Item :class="{ 'mt-0': hasMargin(index) }" :icon="item.icon" :name="item.name" :category="item.category" :rarity="item.rarity" :value="item.value" :date="item.created_at" />
                            </NuxtLink>
                        </SwiperSlide>
                    </Swiper>
                    <button class="carouselPrev ps99ItemsPrev">
                        <i class="fa-solid fa-angle-left"></i>
                    </button>
                    <button class="carouselNext ps99ItemsNext">
                        <i class="fa-solid fa-angle-right"></i>
                    </button>
                </div>
            </section>
            <section>
                <div class="flex items-center justify-between">
                    <h2 class="text-md text-gray-800 dark:text-white font-semibold">Latest Value Changes</h2>
                    <NuxtLink to="/ps-99/value-changes" class="text-sm text-gray-800 dark:text-white hover:text-primary duration-300 font-semibold">View All <i class="fa-solid fa-arrow-right-long ml-1.5"></i></NuxtLink>
                </div>
                <div class="relative mt-3.5">
                    <Swiper :navigation="{ nextEl: '.ps99ValueChangesNext', prevEl: '.ps99ValueChangesPrev', disabledClass: 'carouselButtonDisabled' }" :modules="[SwiperNavigation]" :breakpoints="slideBreakpoints">
                        <SwiperSlide v-for="valueChange in valueChanges" :key="valueChange.item.id">
                            <NuxtLink :to="`/ps-99/item/${valueChange.item.id}`">
                                <ValueChange :icon="valueChange.item.icon" :name="valueChange.item.name" :category="valueChange.item.category" :rarity="valueChange.item.rarity" :oldValue="valueChange.oldValue" :oldValueApplicable="valueChange.oldValueApplicable" :newValue="valueChange.newValue" :newValueApplicable="valueChange.newValueApplicable" :date="valueChange.date" />
                            </NuxtLink>
                        </SwiperSlide>
                    </Swiper>
                    <button class="carouselPrev ps99ValueChangesPrev">
                        <i class="fa-solid fa-angle-left"></i>
                    </button>
                    <button class="carouselNext ps99ValueChangesNext">
                        <i class="fa-solid fa-angle-right"></i>
                    </button>
                </div>
            </section>
        </div>
    </NuxtLayout>
</template>

<script setup lang="ts">
import type { SwiperOptions } from 'swiper/types'
import Item from './components/Item.vue'
import ValueChange from './components/ValueChange.vue'

export interface ItemData {
    id: number;
    name: string;
    icon: string;
    category: string;
    rarity: string;
    value: number | undefined;
    created_at: string;
}

export interface ValueChange {
    id: number;
    item: ItemData;
    oldValue: number;
    oldValueApplicable: boolean;
    newValue: number;
    newValueApplicable: boolean;
    date: string;
}

const { data: items } = await useFetch<ItemData[]>('/api/ps-99/latest/items')
const { data: valueChanges } = await useFetch<ValueChange[]>('/api/ps-99/latest/value-changes')

const hasMargin = (index: number) => {
    if(import.meta.client) {
        const slide = document.querySelectorAll('#latestItems .swiper-slide')[index]
        if (!slide) return false
        // Check the computed style of the margin-top
        const computedStyle = window.getComputedStyle(slide)
        const marginTop = parseFloat(computedStyle.marginTop)
        return marginTop > 0
    }
}

const itemBreakpoints: SwiperOptions['breakpoints'] = {
    1360: {
        spaceBetween: 15,
        slidesPerView: 5,
        grid: {
            rows: 2,
            fill: 'row'
        }
    },
    870: {
        spaceBetween: 15,
        slidesPerView: 4,
        grid: {
            rows: 1,
            fill: 'row'
        }
    },
    620: {
        spaceBetween: 15,
        slidesPerView: 3,
        grid: {
            rows: 1,
            fill: 'row'
        }
    },
    0: {
        spaceBetween: 15,
        slidesPerView: 2,
        grid: {
            rows: 1,
            fill: 'row'
        }
    }
}

const slideBreakpoints = {
    1360: {
        spaceBetween: 15,
        slidesPerView: 5
    },
    870: {
        spaceBetween: 15,
        slidesPerView: 4
    },
    620: {
        spaceBetween: 15,
        slidesPerView: 3
    },
    0: {
        spaceBetween: 15,
        slidesPerView: 2
    }
}

useHead({
    title: 'Pet Simulator 99'
})
</script>